import { ConnectionsProfile } from '@program/shared/connections-profile.model';
import { Program } from '@program/shared/program.model';
import { BaseModel } from '@shared/base.model';
import { Organization } from '@shared/models/organization/organization.model';
import { Country } from '@shared/services/country/country.model';
import { Region } from '@shared/services/region/region.model';

import { ProjectPermissions } from './project-permissions.model';
import { ProjectSpec } from './project-specs.model';
import { ProjectType } from './project-type.enum';
import { ProjectScope } from './project-scope.enum';

export class Project extends BaseModel<Project> {
  address1: string;
  address2: string;
  alternateCurrency: string;
  postalCode: string;
  city: string;
  cod: Date;
  country: Country;
  description: string;
  exchangeRate: number;
  latitude: number;
  lockDateTime: string;
  longitude: number;
  name: string;
  originalProjectCreatorId: string;
  organization: Organization;
  peakKw: number;
  program: Program;
  projectReportName: string;
  projectReportProvider: string;
  projectReportUrl: string;
  projectSpecifications: Array<ProjectSpec>;
  projectStage: string;
  projectType: ProjectType;
  projectVersion: number;
  region: Region;
  status: string;
  tenderProject: boolean;
  totalCapex: number;
  totalCustomers: number;
  totalPV: number;
  totalConnectionsNotSubmitted: number;
  totalConnectionsSubmitted: number;
  totalConnectionsApproved: number;
  totalConnectionsPaid: number;
  totalConnectionsRejected: number;
  totalConnectionsEverSubmitted: number;
  totalConnectionsVerified: number;
  totalConnectionsVerifiedSubmitted: number;
  totalConnectionsVerifiedSubmittable: number;
  totalConnectionsVerifiedSubmittableSubmitted: number;
  connectionsAmountOwed?: number;
  connectionsAmountPaid?: number;
  connectionsAmountPotential?: number;
  connectionsAmountPotentialInCurrency?: number;
  connectionsAuditedPercent: number;
  connectionsAuditSuccessfulPercent: number;
  connectionsAuditedCount: number;
  connectionsAuditSuccessfulCount: number;
  applicationFormComplete: boolean;
  submittedToProgramDateTime: Date;
  /** DT-6472 FR-14: the linked VIDA application view, populated by the project GET. Read-only. */
  linkedApplication?: {
    externalId: string;
    workflowKey: string;
    fetchedDateTime: string;
    gatesSuspended: boolean;
    patch: Record<string, unknown>;
    communities: Record<string, unknown>[];
    /** Non-null exactly when the Expected Grant fields are server-locked (single community's
     *  rate, or the load-weighted blend once multi-community customers are assigned). */
    derivedSubsidyRate?: number | null;
    /** Multi-community only: the weighted-average lines behind the derived rate. */
    derivedSubsidyBreakdown?: { communityName: string; rate: number; assignedCustomers: number }[] | null;
  };
  /** DT-6472 FR-19: the project's own connection-column additions (community dropdown +
   *  settlement string), merged client-side into the program profile by the column builder. */
  linkedConnectionsProfile?: ConnectionsProfile;
  projectPayoutExchangeRate: number;
  projectPayoutCurrency: string;
  connectionsAmountPaidInCurrency: number;
  projectPermissions: ProjectPermissions;
  duplicationCheckDateTime: Date;
  scope: ProjectScope;
  zohoProjectId: string;
  creditProjectType: ProjectType;

  /**
   * External-prefill transport (design FR-10, FR-11, D4). WRITE-ONLY, BY OMISSION.
   *
   * These two are declared here and assigned by the create flow, but they are deliberately NOT
   * read back in the constructor below. That asymmetry is the whole mechanism, and it is load
   * bearing in both directions:
   *
   *  - DECLARED, or the values never reach the wire. `project.service.ts` posts
   *    `JSON.stringify(project)` wholesale, so it needs no change — but an undeclared property
   *    assigned onto a `Project` still serializes, whereas the class contract would not admit it
   *    and `getFormValues` could not set it under `strict`. Without the declaration backend M4a
   *    has no client.
   *  - NOT MAPPED BACK, or they round-trip. Every response is re-wrapped by `new Project(...)`
   *    (`project.service.ts` :167, :182, :196). Assigning these in the constructor would let a GET
   *    echo re-attach a token to the working project and re-send it on the NEXT save, which the
   *    server would read as a fresh link attempt against an already-linked application —
   *    `ALREADY_CLAIMED`, on a save the user thinks is an ordinary edit.
   *
   * Both are optional and stay undefined for every ordinary project, so a normal save is
   * byte-identical to before this ticket.
   */
  externalPrefillToken?: string;
  workflowPayload?: unknown;

  constructor(projectInfo: any) {
    super();
    this.id = projectInfo.id;
    this.alternateCurrency = projectInfo.alternateCurrency;
    this.projectType = projectInfo.projectType;
    this.city = projectInfo.city;
    this.cod = projectInfo.cod;
    this.country = new Country(projectInfo.country || {});
    this.description = projectInfo.description;
    this.exchangeRate = projectInfo.exchangeRate;
    this.latitude = projectInfo.latitude;
    this.lockDateTime = projectInfo.lockDateTime;
    this.longitude = projectInfo.longitude;
    this.name = projectInfo.name;
    this.peakKw = projectInfo.peakKw;
    this.projectVersion = projectInfo.projectVersion;
    this.region = new Region(projectInfo.region || {});
    this.organization = new Organization(projectInfo.organization || {});
    this.status = projectInfo.status;
    this.program = new Program(projectInfo.program || {});
    this.projectStage = projectInfo.projectStage;
    this.tenderProject = projectInfo.tenderProject;
    this.totalCapex = projectInfo.totalCapex;
    this.totalCustomers = projectInfo.totalCustomers;
    this.totalPV = projectInfo.totalPV;
    this.totalConnectionsNotSubmitted = projectInfo.totalConnectionsNotSubmitted;
    this.totalConnectionsSubmitted = projectInfo.totalConnectionsSubmitted;
    this.totalConnectionsApproved = projectInfo.totalConnectionsApproved;
    this.totalConnectionsPaid = projectInfo.totalConnectionsPaid;
    this.totalConnectionsRejected = projectInfo.totalConnectionsRejected;
    this.totalConnectionsEverSubmitted = projectInfo.totalConnectionsEverSubmitted;
    this.totalConnectionsVerified = projectInfo.totalConnectionsVerified;
    this.totalConnectionsVerifiedSubmitted = projectInfo.totalConnectionsVerifiedSubmitted;
    this.totalConnectionsVerifiedSubmittable = projectInfo.totalConnectionsVerifiedSubmittable;
    this.totalConnectionsVerifiedSubmittableSubmitted = projectInfo.totalConnectionsVerifiedSubmittableSubmitted;
    this.connectionsAmountOwed = projectInfo.connectionsAmountOwed;
    this.connectionsAmountPaid = projectInfo.connectionsAmountPaid;
    this.connectionsAmountPotential = projectInfo.connectionsAmountPotential;
    this.connectionsAmountPotentialInCurrency = projectInfo.connectionsAmountPotentialInCurrency;
    this.connectionsAuditedPercent = projectInfo.connectionsAuditedPercent;
    this.connectionsAuditSuccessfulPercent = projectInfo.connectionsAuditSuccessfulPercent;
    this.connectionsAuditedCount = projectInfo.connectionsAuditedCount;
    this.connectionsAuditSuccessfulCount = projectInfo.connectionsAuditSuccessfulCount;
    this.applicationFormComplete = projectInfo.applicationFormComplete;
    this.submittedToProgramDateTime = projectInfo.submittedToProgramDateTime;
    this.linkedApplication = projectInfo.linkedApplication;
    this.linkedConnectionsProfile = projectInfo.linkedConnectionsProfile;

    this.projectSpecifications = [];
    if (projectInfo.projectSpecifications != null && projectInfo.projectSpecifications.length > 0) {
      projectInfo.projectSpecifications.forEach(element => {
        this.projectSpecifications.push(new ProjectSpec(element, element.specificationSource));
      });
    }

    this.projectReportName = projectInfo.projectReportName;
    this.projectReportUrl = projectInfo.projectReportUrl;
    this.projectReportProvider = projectInfo.projectReportProvider;
    this.originalProjectCreatorId = projectInfo.originalProjectCreatorId;
    this.projectPayoutExchangeRate = projectInfo.projectPayoutExchangeRate;
    this.projectPayoutCurrency = projectInfo.projectPayoutCurrency;
    this.connectionsAmountPaidInCurrency = projectInfo.connectionsAmountPaidInCurrency;
    this.projectPermissions = projectInfo.projectPermissions;
    this.duplicationCheckDateTime = projectInfo.duplicationCheckDateTime;
    this.address1 = projectInfo.address1;
    this.address2 = projectInfo.address2;
    this.postalCode = projectInfo.postalCode;
    this.scope = projectInfo.scope;
    this.zohoProjectId = projectInfo.zohoProjectId;
    this.creditProjectType = projectInfo.creditProjectType;
  }
}
